import React, {useEffect, useState, useCallback} from 'react';
import {Link} from 'react-router-dom';
import {useSelector} from 'react-redux';
import styled from 'styled-components';
import {Button, Icon, Item, Label, Loader, Message} from 'semantic-ui-react';
import {listMine, deleteSample} from './api';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  gap: 12px;
  flex-wrap: wrap;
`;

const Stat = styled.span`
  margin-right: 12px;
  font-size: 0.9em;
  color: var(--br-muted);
`;

const Description = styled.p`
  color: var(--br-muted);
  margin: 0 0 16px 0;
`;

const SampleChaptersTab = () => {
  const idToken = useSelector((s) => s.user.idToken);
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!idToken) return;
    try {
      const data = await listMine(idToken);
      setItems(data);
    } catch (e) {
      setError(e.message);
    }
  }, [idToken]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this sample chapter and all its ratings?')) return;
    try {
      await deleteSample(idToken, id);
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div>
      <Header>
        <Description>
          Publish only the first chapter of a book — text, audio, or both — and
          let readers tell you whether they would keep reading. Use variants
          (A/B) to test alternative openings.
        </Description>
        <div>
          <Button as={Link} to="/sample-chapters/discover" basic>
            <Icon name="search" /> Browse
          </Button>
          <Button primary as={Link} to="/sample-chapters/new">
            <Icon name="plus" /> New sample
          </Button>
        </div>
      </Header>

      {error && <Message error content={error} />}
      {!items && <Loader active inline="centered" />}
      {items && items.length === 0 && (
        <Message info>
          You haven't created any sample chapters yet.{' '}
          <Link to="/sample-chapters/new">Create your first sample</Link>.
        </Message>
      )}
      {items && items.length > 0 && (
        <Item.Group divided>
          {items.map((s) => (
            <Item key={s._id}>
              <Item.Content>
                <Item.Header>
                  <Link to={`/sample-chapters/edit/${s._id}`}>{s.title}</Link>{' '}
                  <Label size="tiny">Variant {s.variantLabel}</Label>{' '}
                  {s.listed ? (
                    <Label color="green" size="tiny">
                      Listed
                    </Label>
                  ) : (
                    <Label size="tiny">Draft</Label>
                  )}
                  {s.audioUrl ? (
                    <Label color="violet" size="tiny">
                      <Icon name="volume up" /> Audio
                    </Label>
                  ) : (
                    <Label size="tiny">
                      <Icon name="font" /> Text only
                    </Label>
                  )}
                </Item.Header>
                <Item.Meta>
                  <Stat>
                    <Icon name="users" /> {s.ratingsCount || 0} ratings
                  </Stat>
                  <Stat>
                    <Icon name="thumbs up" color="green" /> {s.yesCount || 0}
                  </Stat>
                  <Stat>
                    <Icon name="thumbs down" color="grey" /> {s.noCount || 0}
                  </Stat>
                  <Stat>
                    <Icon name="mail" /> {s.contactOptInCount || 0} contact
                    opt-ins
                  </Stat>
                </Item.Meta>
                <Item.Extra>
                  <Button as={Link} to={`/sample-chapters/read/${s._id}`} basic size="small">
                    <Icon name="eye" /> Preview
                  </Button>
                  <Button as={Link} to={`/sample-chapters/edit/${s._id}`} size="small">
                    <Icon name="edit" /> Edit
                  </Button>
                  <Button
                    basic
                    color="red"
                    size="small"
                    onClick={() => handleDelete(s._id)}
                  >
                    <Icon name="trash" /> Delete
                  </Button>
                </Item.Extra>
              </Item.Content>
            </Item>
          ))}
        </Item.Group>
      )}
    </div>
  );
};

export default SampleChaptersTab;
